/**
 * Reports/Analytics View
 * Monthly analysis with charts and percentages
 */

export class Reports {
  constructor(container) {
    this.container = container;
    this.transactions = [];
    this.monthlyData = {};
    this.charts = {};
    this.updateHandler = null;
    this.chartJsLoaded = false;
  }

  async render() {
    // Load Chart.js if not already loaded
    if (!window.Chart) {
      await this.loadChartJS();
    }

    this.container.innerHTML = `
      <div class="page-header">
        <h1 class="page-title">Análise Mensal</h1>
        <p class="page-subtitle">Relatórios e insights financeiros</p>
      </div>

      <!-- Period Selector -->
      <div class="card">
        <div class="card-body">
          <div class="period-selector">
            <label for="analysis-period">Período:</label>
            <select id="analysis-period" class="form-control" style="max-width: 300px;">
              <option value="current">Mês Atual</option>
              <option value="last3">Últimos 3 Meses</option>
              <option value="last6">Últimos 6 Meses</option>
              <option value="last12">Últimos 12 Meses</option>
              <option value="all">Todo o Período</option>
            </select>
          </div>
        </div>
      </div>

      <!-- Summary Cards -->
      <div class="grid grid-cols-4" style="margin-top: var(--spacing-6);">
        <div class="summary-card">
          <h3>Total de Entradas</h3>
          <p class="amount" id="report-total-entradas" style="color: var(--color-success-dark);">R$ 0,00</p>
        </div>
        <div class="summary-card">
          <h3>Total de Saídas</h3>
          <p class="amount" id="report-total-saidas" style="color: var(--color-danger-dark);">R$ 0,00</p>
        </div>
        <div class="summary-card">
          <h3>Saldo Líquido</h3>
          <p class="amount" id="report-saldo">R$ 0,00</p>
        </div>
        <div class="summary-card">
          <h3>Margem (%)</h3>
          <p class="amount" id="report-margem">0%</p>
        </div>
      </div>

      <!-- Charts Row 1 -->
      <div class="grid grid-cols-2" style="margin-top: var(--spacing-6);">
        <div class="card">
          <div class="card-header">
            <h2 class="card-title">Receitas vs Despesas</h2>
          </div>
          <div class="card-body">
            <canvas id="chart-income-expense" height="300"></canvas>
          </div>
        </div>

        <div class="card">
          <div class="card-header">
            <h2 class="card-title">Distribuição de Despesas</h2>
          </div>
          <div class="card-body">
            <canvas id="chart-expense-distribution" height="300"></canvas>
          </div>
        </div>
      </div>

      <!-- Percentage Analysis -->
      <div class="card" style="margin-top: var(--spacing-6);">
        <div class="card-header">
          <h2 class="card-title">Análise Percentual - Despesas sobre Receita</h2>
          <span class="badge badge-info" id="expense-percentage-badge">0%</span>
        </div>
        <div class="card-body">
          <div id="percentage-analysis">
            <!-- Will be populated dynamically -->
          </div>
        </div>
      </div>

      <!-- Category Breakdown Table -->
      <div class="card" style="margin-top: var(--spacing-6);">
        <div class="card-header">
          <h2 class="card-title">Detalhamento por Categoria</h2>
        </div>
        <div class="card-body" style="padding: 0;">
          <div class="table-responsive">
            <table class="table">
              <thead>
                <tr>
                  <th>Categoria</th>
                  <th class="text-right">Valor</th>
                  <th class="text-right">% das Despesas</th>
                  <th class="text-right">% da Receita</th>
                  <th>Tendência</th>
                </tr>
              </thead>
              <tbody id="category-breakdown-tbody">
                <tr>
                  <td colspan="5" class="text-center" style="padding: var(--spacing-8);">
                    Carregando...
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Monthly Comparison Chart -->
      <div class="card" style="margin-top: var(--spacing-6);">
        <div class="card-header">
          <h2 class="card-title">Evolução Mensal</h2>
        </div>
        <div class="card-body">
          <canvas id="chart-monthly-trend" height="200"></canvas>
        </div>
      </div>
    `;

    this.loadData();
    this.attachEventListeners();

    // Listen for transaction updates
    if (!this.updateHandler) {
      this.updateHandler = () => {
        console.log('📊 Reports: Recarregando análise...');
        this.loadData();
      };
      window.addEventListener('transactionsUpdated', this.updateHandler);
    }
  }

  async loadChartJS() {
    return new Promise((resolve, reject) => {
      if (window.Chart) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/chart.js@4.4.0/dist/chart.umd.min.js';
      script.onload = () => {
        this.chartJsLoaded = true;
        resolve();
      };
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  attachEventListeners() {
    const periodSelect = this.container.querySelector('#analysis-period');
    if (periodSelect) {
      periodSelect.addEventListener('change', () => this.loadData());
    }
  }

  loadData() {
    const stored = localStorage.getItem('clinicaDaher_cashflow');
    if (stored) {
      try {
        this.transactions = JSON.parse(stored);
      } catch (e) {
        console.error('Error loading transactions:', e);
        this.transactions = [];
      }
    }

    this.filterByPeriod();
    this.processData();
    this.updateSummary();
    this.renderCharts();
    this.renderPercentageAnalysis();
    this.renderCategoryBreakdown();
  }

  filterByPeriod() {
    const period = this.container.querySelector('#analysis-period')?.value || 'current';
    const now = new Date();
    let startDate = null;

    switch (period) {
      case 'current':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case 'last3':
        startDate = new Date(now.getFullYear(), now.getMonth() - 3, 1);
        break;
      case 'last6':
        startDate = new Date(now.getFullYear(), now.getMonth() - 6, 1);
        break;
      case 'last12':
        startDate = new Date(now.getFullYear(), now.getMonth() - 12, 1);
        break;
      case 'all':
        startDate = null;
        break;
    }

    if (startDate) {
      const startDateStr = startDate.toISOString().split('T')[0];
      this.transactions = this.transactions.filter(t => t.date >= startDateStr);
    }
  }

  processData() {
    this.monthlyData = {};

    this.transactions.forEach(t => {
      const [year, month] = t.date.split('-');
      const monthKey = `${year}-${month}`;

      if (!this.monthlyData[monthKey]) {
        this.monthlyData[monthKey] = {
          entradas: {},
          saidas: {},
          totalEntradas: 0,
          totalSaidas: 0
        };
      }

      const amount = typeof t.amount === 'number' ? t.amount : parseFloat(t.amount) || 0;
      const monthData = this.monthlyData[monthKey];

      if (t.type === 'entrada') {
        monthData.totalEntradas += amount;
        monthData.entradas[t.category] = (monthData.entradas[t.category] || 0) + amount;
      } else if (t.type === 'saida') {
        monthData.totalSaidas += amount;
        monthData.saidas[t.category] = (monthData.saidas[t.category] || 0) + amount;
      }
    });
  }

  updateSummary() {
    const totalEntradas = this.transactions
      .filter(t => t.type === 'entrada')
      .reduce((sum, t) => sum + (typeof t.amount === 'number' ? t.amount : parseFloat(t.amount) || 0), 0);

    const totalSaidas = this.transactions
      .filter(t => t.type === 'saida')
      .reduce((sum, t) => sum + (typeof t.amount === 'number' ? t.amount : parseFloat(t.amount) || 0), 0);

    const saldo = totalEntradas - totalSaidas;
    const margem = totalEntradas > 0 ? ((saldo / totalEntradas) * 100) : 0;
    const expensePercentage = totalEntradas > 0 ? ((totalSaidas / totalEntradas) * 100) : 0;

    const entradasEl = this.container.querySelector('#report-total-entradas');
    const saidasEl = this.container.querySelector('#report-total-saidas');
    const saldoEl = this.container.querySelector('#report-saldo');
    const margemEl = this.container.querySelector('#report-margem');
    const expensePercentageBadge = this.container.querySelector('#expense-percentage-badge');

    if (entradasEl) entradasEl.textContent = this.formatCurrency(totalEntradas);
    if (saidasEl) saidasEl.textContent = this.formatCurrency(totalSaidas);
    if (saldoEl) {
      saldoEl.textContent = this.formatCurrency(saldo);
      saldoEl.style.color = saldo >= 0 ? 'var(--color-success-dark)' : 'var(--color-danger-dark)';
    }
    if (margemEl) {
      margemEl.textContent = margem.toFixed(1) + '%';
      margemEl.style.color = margem >= 20 ? 'var(--color-success-dark)' : margem >= 10 ? 'var(--color-warning-dark)' : 'var(--color-danger-dark)';
    }
    if (expensePercentageBadge) {
      expensePercentageBadge.textContent = expensePercentage.toFixed(1) + '% das receitas';
      expensePercentageBadge.className = 'badge badge-' + (expensePercentage > 90 ? 'danger' : expensePercentage > 70 ? 'warning' : 'success');
    }
  }

  renderCharts() {
    if (!window.Chart) {
      console.error('Chart.js not loaded');
      return;
    }

    this.renderIncomeExpenseChart();
    this.renderExpenseDistributionChart();
    this.renderMonthlyTrendChart();
  }

  renderIncomeExpenseChart() {
    const canvas = this.container.querySelector('#chart-income-expense');
    if (!canvas) return;

    if (this.charts.incomeExpense) {
      this.charts.incomeExpense.destroy();
    }

    const months = Object.keys(this.monthlyData).sort();
    const labels = months.map(m => this.getMonthName(m));
    const entradas = months.map(m => this.monthlyData[m].totalEntradas);
    const saidas = months.map(m => this.monthlyData[m].totalSaidas);

    this.charts.incomeExpense = new Chart(canvas, {
      type: 'bar',
      data: {
        labels: labels,
        datasets: [
          {
            label: 'Entradas',
            data: entradas,
            backgroundColor: '#10B981',
            borderColor: '#059669',
            borderWidth: 2
          },
          {
            label: 'Saídas',
            data: saidas,
            backgroundColor: '#EF4444',
            borderColor: '#DC2626',
            borderWidth: 2
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: 'top'
          },
          tooltip: {
            callbacks: {
              label: (context) => {
                return context.dataset.label + ': ' + this.formatCurrency(context.parsed.y);
              }
            }
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              callback: (value) => 'R$ ' + value.toLocaleString('pt-BR')
            }
          }
        }
      }
    });
  }

  renderExpenseDistributionChart() {
    const canvas = this.container.querySelector('#chart-expense-distribution');
    if (!canvas) return;

    if (this.charts.expenseDistribution) {
      this.charts.expenseDistribution.destroy();
    }

    // Aggregate all expenses by category
    const categoryTotals = {};
    Object.values(this.monthlyData).forEach(month => {
      Object.entries(month.saidas).forEach(([category, amount]) => {
        categoryTotals[category] = (categoryTotals[category] || 0) + amount;
      });
    });

    const categories = Object.keys(categoryTotals);
    const values = Object.values(categoryTotals);
    const colors = this.generateColors(categories.length);

    this.charts.expenseDistribution = new Chart(canvas, {
      type: 'doughnut',
      data: {
        labels: categories,
        datasets: [{
          data: values,
          backgroundColor: colors,
          borderWidth: 2,
          borderColor: '#ffffff'
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: 'right'
          },
          tooltip: {
            callbacks: {
              label: (context) => {
                const total = values.reduce((a, b) => a + b, 0);
                const percentage = ((context.parsed / total) * 100).toFixed(1);
                return context.label + ': ' + this.formatCurrency(context.parsed) + ' (' + percentage + '%)';
              }
            }
          }
        }
      }
    });
  }

  renderMonthlyTrendChart() {
    const canvas = this.container.querySelector('#chart-monthly-trend');
    if (!canvas) return;

    if (this.charts.monthlyTrend) {
      this.charts.monthlyTrend.destroy();
    }

    const months = Object.keys(this.monthlyData).sort();
    const labels = months.map(m => this.getMonthName(m));
    const saldos = months.map(m => this.monthlyData[m].totalEntradas - this.monthlyData[m].totalSaidas);

    this.charts.monthlyTrend = new Chart(canvas, {
      type: 'line',
      data: {
        labels: labels,
        datasets: [{
          label: 'Saldo Mensal',
          data: saldos,
          borderColor: '#2196F3',
          backgroundColor: 'rgba(33, 150, 243, 0.1)',
          borderWidth: 3,
          fill: true,
          tension: 0.4
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            callbacks: {
              label: (context) => {
                return 'Saldo: ' + this.formatCurrency(context.parsed.y);
              }
            }
          }
        },
        scales: {
          y: {
            ticks: {
              callback: (value) => 'R$ ' + value.toLocaleString('pt-BR')
            }
          }
        }
      }
    });
  }

  renderPercentageAnalysis() {
    const container = this.container.querySelector('#percentage-analysis');
    if (!container) return;

    const totalEntradas = this.transactions
      .filter(t => t.type === 'entrada')
      .reduce((sum, t) => sum + (typeof t.amount === 'number' ? t.amount : parseFloat(t.amount) || 0), 0);

    if (totalEntradas === 0) {
      container.innerHTML = '<p class="text-center" style="color: var(--gray-500);">Sem receitas no período selecionado.</p>';
      return;
    }

    // Calculate category percentages
    const categoryTotals = {};
    this.transactions
      .filter(t => t.type === 'saida')
      .forEach(t => {
        const amount = typeof t.amount === 'number' ? t.amount : parseFloat(t.amount) || 0;
        categoryTotals[t.category] = (categoryTotals[t.category] || 0) + amount;
      });

    const sortedCategories = Object.entries(categoryTotals)
      .sort(([, a], [, b]) => b - a);

    container.innerHTML = sortedCategories.map(([category, amount]) => {
      const percentage = (amount / totalEntradas) * 100;
      const color = this.getPercentageColor(percentage);

      return `
        <div class="percentage-bar-container" style="margin-bottom: var(--spacing-4);">
          <div style="display: flex; justify-content: space-between; margin-bottom: var(--spacing-2);">
            <span style="font-weight: 600;">${category}</span>
            <span style="color: var(--gray-600);">
              ${this.formatCurrency(amount)}
              <span style="color: ${color}; font-weight: 600;">(${percentage.toFixed(1)}%)</span>
            </span>
          </div>
          <div class="percentage-bar">
            <div class="percentage-bar-fill" style="width: ${Math.min(percentage, 100)}%; background-color: ${color};"></div>
          </div>
        </div>
      `;
    }).join('');
  }

  renderCategoryBreakdown() {
    const tbody = this.container.querySelector('#category-breakdown-tbody');
    if (!tbody) return;

    const totalEntradas = this.transactions
      .filter(t => t.type === 'entrada')
      .reduce((sum, t) => sum + (typeof t.amount === 'number' ? t.amount : parseFloat(t.amount) || 0), 0);

    const totalSaidas = this.transactions
      .filter(t => t.type === 'saida')
      .reduce((sum, t) => sum + (typeof t.amount === 'number' ? t.amount : parseFloat(t.amount) || 0), 0);

    const categoryTotals = {};
    this.transactions
      .filter(t => t.type === 'saida')
      .forEach(t => {
        const amount = typeof t.amount === 'number' ? t.amount : parseFloat(t.amount) || 0;
        categoryTotals[t.category] = (categoryTotals[t.category] || 0) + amount;
      });

    if (Object.keys(categoryTotals).length === 0) {
      tbody.innerHTML = '<tr><td colspan="5" class="text-center" style="padding: var(--spacing-8); color: var(--gray-500);">Sem despesas no período</td></tr>';
      return;
    }

    const sortedCategories = Object.entries(categoryTotals)
      .sort(([, a], [, b]) => b - a);

    tbody.innerHTML = sortedCategories.map(([category, amount]) => {
      const percentOfExpenses = (amount / totalSaidas) * 100;
      const percentOfRevenue = totalEntradas > 0 ? (amount / totalEntradas) * 100 : 0;
      const trend = this.getCategoryTrend(category);

      return `
        <tr>
          <td><strong>${category}</strong></td>
          <td class="text-right">${this.formatCurrency(amount)}</td>
          <td class="text-right">${percentOfExpenses.toFixed(1)}%</td>
          <td class="text-right" style="color: ${this.getPercentageColor(percentOfRevenue)};">
            ${percentOfRevenue.toFixed(1)}%
          </td>
          <td>${trend}</td>
        </tr>
      `;
    }).join('');
  }

  getCategoryTrend(category) {
    const months = Object.keys(this.monthlyData).sort();
    if (months.length < 2) return '-';

    const lastMonth = this.monthlyData[months[months.length - 1]];
    const prevMonth = this.monthlyData[months[months.length - 2]];

    const lastAmount = lastMonth.saidas[category] || 0;
    const prevAmount = prevMonth.saidas[category] || 0;

    if (prevAmount === 0) return '🆕';

    const change = ((lastAmount - prevAmount) / prevAmount) * 100;

    if (change > 10) return '📈 +' + change.toFixed(0) + '%';
    if (change < -10) return '📉 ' + change.toFixed(0) + '%';
    return '➡️ Estável';
  }

  getPercentageColor(percentage) {
    if (percentage > 30) return '#DC2626'; // Red
    if (percentage > 20) return '#F59E0B'; // Yellow/Orange
    if (percentage > 10) return '#2196F3'; // Blue
    return '#10B981'; // Green
  }

  generateColors(count) {
    const colors = [
      '#EF4444', '#F59E0B', '#10B981', '#3B82F6', '#8B5CF6',
      '#EC4899', '#14B8A6', '#F97316', '#06B6D4', '#84CC16'
    ];

    const result = [];
    for (let i = 0; i < count; i++) {
      result.push(colors[i % colors.length]);
    }
    return result;
  }

  getMonthName(monthKey) {
    const [year, month] = monthKey.split('-');
    const date = new Date(year, parseInt(month) - 1);
    return date.toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' });
  }

  formatCurrency(value) {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  }

  destroy() {
    // Destroy all charts
    Object.values(this.charts).forEach(chart => {
      if (chart) chart.destroy();
    });
    this.charts = {};

    // Remove event listener
    if (this.updateHandler) {
      window.removeEventListener('transactionsUpdated', this.updateHandler);
      this.updateHandler = null;
    }
  }
}
